/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as createUUID } from "uuid";

import { getInteractionRate } from "@atlassian/wac-ufo-config";
import { LabelStack } from "@atlassian/wac-ufo-interaction-context";
import {
    InteractionType,
    abort,
    addCustomData,
    addError,
    addNewInteraction,
    tryComplete,
    CustomData
} from "@atlassian/wac-ufo-interaction-metrics";
import interactions from "@atlassian/wac-ufo-interaction-metrics/src/common/constants";

import { ProductKey } from "@/libraries/signup/bxpkit-core/src/constants";

import { Cookie } from "../../../utils/cookie";

import { UfoEventName, UfoExperienceStatus, UfoLabelStack } from "./ufo.types";

const { ABORTED } = UfoExperienceStatus;

export const SignupUFOLoggingActionTimeoutError = new Error(`Action times out`);

export type AddUFOErrorOptions = {
    error: any;
    interactionId: string;
    errorType: string;
    errorMessage: string;
    name: string;
    labelStack: { name: string; id?: string }[];
    config: { isQueryParamCheck: boolean };
};

export type SignupAddUFOInteractionOptions = {
    interactionName: string;
    type: InteractionType;
    timeout?: number;
    labelStack?: { name: string; id?: string }[];
};

export type OperateWithSignupUFOLoggingOptions = {
    interactionId: string;
    interactionName: string;
    type: InteractionType;
    timeout?: number;
    labelStack?: { name: string; id?: string }[];
    errorContext?: string;
};

export async function operateWithSignupUFOLogging<T>(
    options: OperateWithSignupUFOLoggingOptions,
    callback: () => Promise<T>,
    errorLogCallback?: (error: any) => unknown,
    responseLogCallback?: (response: any) => unknown
): Promise<T> {
    const {
        interactionId,
        interactionName,
        type = "transition",
        timeout,
        labelStack = [],
        errorContext
    } = options;
    const baseStack = _buildUFOLoggerBaseStack();
    addNewInteraction(
        interactionId,
        interactionName,
        type,
        Date.now(),
        getInteractionRate(
            interactionName,
            type === "segment" ? "legacy" : type
        ),
        labelStack
    );
    addCustomData(interactionId, labelStack, baseStack);

    if (timeout) {
        const interaction = interactions.get(interactionId);
        interaction?.changeTimeout(timeout);
    }
    try {
        const res = await callback();
        if (responseLogCallback) {
            responseLogCallback(res);
        }
        tryComplete(interactionId, Date.now());
        return res;
    } catch (error: any) {
        if (errorLogCallback) {
            errorLogCallback(error);
            throw error;
        }
        const errorStack = _buildUFOLoggerErrorStack(error);
        const errorStackArray: LabelStack = Object.keys(errorStack)
            .map((key) =>
                !!errorStack[key] ? { name: `${key}:${errorStack[key]}` } : null
            )
            .filter((value): value is { name: string } => !!value);
        addError(
            interactionId,
            interactionName,
            errorStackArray,
            SignupGetUFOErrorType(error),
            `Error occurred on call: ${errorContext}`
        );
        abort(interactionId, "unload");
        throw error;
    }
}

export function SignupAddUFOInteraction(
    options: SignupAddUFOInteractionOptions
): string {
    const { interactionName, type, timeout, labelStack } = options;
    const formSubmitInteractionId = createUUID();

    addNewInteraction(
        formSubmitInteractionId,
        interactionName,
        type,
        Date.now(),
        getInteractionRate(
            interactionName,
            type === "segment" ? "legacy" : type
        ),
        labelStack ?? []
    );
    if (timeout) {
        const interaction = interactions.get(formSubmitInteractionId);
        interaction?.changeTimeout(timeout);
    }
    return formSubmitInteractionId;
}

export function SignupAddUFOInteractionError(options: AddUFOErrorOptions) {
    const {
        error,
        interactionId,
        errorType,
        name,
        labelStack,
        errorMessage,
        config
    } = options;
    const errorCode = error?.response?.status;
    if (errorCode) {
        labelStack.push({
            name: `errorCode:${errorCode}`
        });
    }
    const errorId = error?.response?.headers?.get("atl-traceid");
    if (errorId) {
        labelStack.push({
            name: `atlTraceId:${errorId}`
        });
    }
    const errorUrl = window?.location?.href;
    if (config.isQueryParamCheck && errorUrl) {
        const url = new URL(errorUrl);
        const requestUrl = error?.url;
        labelStack.push({
            name: `pathName:${url.pathname}`
        });
        if (requestUrl) {
            labelStack.push({
                name: `requestPath:${new URL(requestUrl).pathname}`
            });
        }
        const product = url.searchParams.get("product");
        if (product) {
            labelStack.push({
                name: `product:${product}`
            });
        }
        const edition = url.searchParams.get("edition");
        if (product) {
            labelStack.push({
                name: `edition:${edition}`
            });
        }
        const bundle = url.searchParams.get("bundle");
        if (product) {
            labelStack.push({
                name: `bundle:${bundle}`
            });
        }
    }

    addError(interactionId, name, labelStack, errorType, errorMessage);
}

export function SignupUFOInteractionComplete(interactionId: string) {
    tryComplete(interactionId, Date.now());
}

export function SignupUFOInteractionAbortAndUnload(interactionId: string) {
    abort(interactionId, "unload");
}

export function SignupGetUFOErrorType(error: any) {
    let errorType;
    if (error == SignupUFOLoggingActionTimeoutError) {
        errorType = "timeout";
    } else if (!!error?.response?.status) {
        errorType = "http_exception";
    } else if (error?.name) {
        errorType = error.name;
    } else {
        errorType = "react_error";
    }
    return errorType;
}

type UFOStatelessEventOptions = {
    experienceName: UfoEventName;
    experienceStatus: UfoExperienceStatus;
    interactionType?: InteractionType;
    ufoLogger?: UfoLabelStack;
    error?: any;
    errorName?: string;
    errorMessage?: string;
};

export function trackAndCompleteUFOEvent(options: UFOStatelessEventOptions) {
    const {
        experienceName,
        experienceStatus,
        interactionType = "transition",
        ufoLogger = [],
        error = undefined,
        errorName = "",
        errorMessage = ""
    } = options;

    const interactionId = createUUID();

    // UFO exposes 'labelStack', which acts similar to deferred info logger.
    // These logs surface to Splunk, but not SignalFX
    const labelStack = _buildUFOLoggerLabelStack({
        labelStack: ufoLogger,
        error
    });

    // Initialize UFO event
    addNewInteraction(
        interactionId,
        experienceName,
        interactionType,
        Date.now(),
        getInteractionRate(
            experienceName,
            interactionType === "segment" ? "legacy" : interactionType
        ),
        labelStack
    );

    if (experienceStatus === ABORTED) {
        // Add error meta data to UFO event
        addError(
            interactionId,
            errorName,
            labelStack,
            SignupGetUFOErrorType(error),
            errorMessage
        );

        // Send ABORTED event to UFO event queue
        abort(interactionId, "unload");
    } else {
        // Send SUCCEEDED event to UFO event queue
        tryComplete(interactionId, Date.now());
    }
}

type UfoLoggerOptions = {
    error?: any;
    labelStack?: UfoLabelStack;
};

function _buildUFOLoggerLabelStack(options: UfoLoggerOptions) {
    const { labelStack = [], error = undefined } = options;
    const baseStack = _buildUFOLoggerBaseStack();
    const errorStack = _buildUFOLoggerErrorStack(error);
    const baseStackArray: LabelStack = Object.keys(baseStack)
        .map((key) =>
            !!baseStack[key] ? { name: `${key}:${baseStack[key]}` } : null
        )
        .filter((value): value is { name: string } => !!value);
    const errorStackArray: LabelStack = Object.keys(errorStack)
        .map((key) =>
            !!errorStack[key] ? { name: `${key}:${errorStack[key]}` } : null
        )
        .filter((value): value is { name: string } => !!value);

    return [
        ...labelStack,
        ...(baseStackArray ?? []),
        ...(errorStackArray ?? [])
    ];
}

function _buildUFOLoggerBaseStack(): { [key: string]: string | null } {
    // We want to use 'analyticsAnonymousId' to link the interaction to the user via Splunk logs
    const analyticsAnonymousId = Cookie.readCookie("ajs_anonymous_id");

    // Build base stack for UFO logger
    const browserUrl = new URL(window?.location?.href);
    const browserPath = browserUrl?.pathname;
    const product = browserUrl.searchParams.get("product");
    const bundle = browserUrl.searchParams.get("bundle");
    const edition = browserUrl.searchParams.get("edition");

    return {
        anonymousId: analyticsAnonymousId,
        browserPath,
        product,
        bundle,
        edition
    };
}

function _buildUFOLoggerErrorStack(error: any): {
    [key: string]: string | null | undefined;
} {
    // Build error stack if error present
    let errorUrl = null;
    let errorReqPath = null;
    let errorStatusCode = null;
    if (error) {
        if (error.url && typeof error.url === "string") {
            try {
                errorUrl = new URL(error.url);
                errorReqPath = errorUrl
                    ? new URL(errorUrl).pathname
                    : undefined;
                errorStatusCode = error.response.status;
            } catch (e) {
                console.log("error building UFO error stack: ", e);
            }
        }
    }

    return {
        errorReqPath,
        errorCode: errorStatusCode
    };
}

type BuildCustomDataUfoOptions = {
    customData?: CustomData;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
};

export function buildCustomDataUFO(options: BuildCustomDataUfoOptions) {
    const { error = undefined, customData = {} } = options;

    // We want to use 'analyticsAnonymousId' to link the interaction to the user via Splunk logs
    const analyticsAnonymousId = Cookie.readCookie("ajs_anonymous_id");

    // This can be populated by bifrost template that contains user signup traits
    const signupUserTraits = window.signupUserTraits;

    // Build base stack for UFO logger
    const browserUrl = new URL(window?.location?.href);
    const browserPath = browserUrl?.pathname;
    const product = browserUrl.searchParams.get("product");
    const bundle = browserUrl.searchParams.get("bundle");
    const edition = browserUrl.searchParams.get("edition");

    const baseData = {
        ...(analyticsAnonymousId ? { analyticsAnonymousId } : {}),
        ...(signupUserTraits ? { signupUserTraits } : {}),
        ...(browserPath ? { browserPath } : {}),
        ...(product ? { product } : {}),
        ...(bundle ? { bundle } : {}),
        ...(edition ? { edition } : {})
    };

    let errorUrl = null;
    let errorReqPath = null;
    let errorStatusCode = null;

    // Build error data if error present
    if (error) {
        if (error.url && typeof error.url === "string") {
            try {
                errorUrl = new URL(error.url);
                errorReqPath = errorUrl
                    ? new URL(errorUrl).pathname
                    : undefined;
                errorStatusCode = error.response.status;
            } catch (err) {
                console.log("error building UFO error custom data: ", err);
            }
        }
    }

    const errorData = {
        ...(errorReqPath ? { errorReqPath } : {}),
        ...(errorStatusCode ? { errorStatusCode } : {})
    };

    return {
        ...baseData,
        ...errorData,
        ...customData
    };
}

export function getProductCohortByKey(
    productKey: ProductKey | string | undefined
) {
    if (!productKey) {
        return "none";
    }

    switch (productKey) {
        case ProductKey.CONFLUENCE:
            return "confluence";
        case ProductKey.JIRA_SOFTWARE:
            return "jira-software";
        case ProductKey.JIRA_WORK_MANAGEMENT:
            return "jira-work-management";
        case ProductKey.JIRA_SERVICE_MANAGEMENT:
            return "jira-service-management";
        case ProductKey.JIRA_PRODUCT_DISCOVERY:
            return "jira-product-discovery";
        case ProductKey.OPSGENIE:
            return "opsgenie";
        default:
            return "other";
    }
}

export function getSiteTypeCohort(isNewSite: boolean) {
    return isNewSite ? "new-site" : "existing-site";
}
